import { trackUsage } from '@creditsights/api-ui-web';
import { useMemo } from 'react';

interface UseTrackUsageResult {
  submitPrompt: (options: {
    sessionId: string;
    seqId: number;
    prompt: string;
    isSuggested: boolean;
    isHistorical: boolean;
    theme: string;
  }) => void;
  createSession: (options: { sessionId: string }) => void;
  openHistory: (options: { sessionId?: string }) => void;
  returnToChat: (options: { sessionId?: string }) => void;
  clickOnHistoryItem: (options: { sessionId: string; historySessionId: string }) => void;
  expandChatScreen: (options: { sessionId?: string }) => void;
  minimizeChatScreen: (options: { sessionId?: string }) => void;
  copyResponse: (options: { sessionId: string; seqId: number }) => void;
  regenerateResponse: (options: { sessionId: string; seqId: number }) => void;
  submitFeedback: (options: { sessionId: string; seqId: number; thumbsUp: boolean }) => void;
  popoutChat: (options: { sessionId: string }) => void;
}

const EVENT_PREFIX = 'GENIE_';

const buildOptions = (options: Object): { key: string; value: string }[] =>
  Object.entries(options).map(([key, rawValue]) => {
    let value;

    switch (typeof rawValue) {
      case 'boolean':
        value = rawValue ? 'Yes' : 'No';
        break;
      case 'number':
        value = `${rawValue}`;
        break;
      default:
        value = rawValue;
        break;
    }

    return { key, value };
  });

export const useTrackUsage = (): UseTrackUsageResult => {
  const result = useMemo<UseTrackUsageResult>(
    () => ({
      submitPrompt(options) {
        trackUsage(`${EVENT_PREFIX}SUBMIT_PROMPT`, buildOptions(options));
      },
      createSession(options) {
        trackUsage(`${EVENT_PREFIX}CREATE_SESSION`, buildOptions(options));
      },
      openHistory(options) {
        trackUsage(`${EVENT_PREFIX}OPEN_HISTORY`, buildOptions(options));
      },
      returnToChat(options) {
        trackUsage(`${EVENT_PREFIX}RETURN_TO_CHAT`, buildOptions(options));
      },
      clickOnHistoryItem(options) {
        trackUsage(`${EVENT_PREFIX}CLICK_ON_HISTORY_ITEM`, buildOptions(options));
      },
      expandChatScreen(options) {
        trackUsage(`${EVENT_PREFIX}EXPAND_CHAT_SCREEN`, buildOptions(options));
      },
      minimizeChatScreen(options) {
        trackUsage(`${EVENT_PREFIX}MINIMIZE_CHAT_SCREEN`, buildOptions(options));
      },
      copyResponse(options) {
        trackUsage(`${EVENT_PREFIX}COPY_RESPONSE`, buildOptions(options));
      },
      regenerateResponse(options) {
        trackUsage(`${EVENT_PREFIX}REGENERATE_RESPONSE`, buildOptions(options));
      },
      submitFeedback(options) {
        trackUsage(`${EVENT_PREFIX}SUBMIT_FEEDBACK`, buildOptions(options));
      },
      popoutChat(options) {
        trackUsage(`${EVENT_PREFIX}POPOUT_CHAT`, buildOptions(options));
      },
    }),
    []
  );

  return result;
};
